import React, { useEffect, useMemo } from "react";
import { useI18n } from "@bookingcom/lingojs-react";
import { Divider } from "@bookingcom/bui-react";
import { UIBrandedFareFeature, UIBrandedFareInfo } from "@flights/types";
import { getFeaturesToRender, mergeFeaturesInOrder } from "../../utils/features";
import FareFeature from "../FareFeature";
import styles from "./FareFeatures.module.css";
import Frame from "../../../components/elements/Frame";
import FareFeatureHeading from "./FareFeatureHeading";
import useUserAgent from "../../../hooks/useUserAgent";
import { CHANGE_FEATURES, overrideSeatSelectedAtAirlineCopy } from "@flights/branded-fares";
import flights_apex_web_bf_features_in_single_list from "../../../utils/experiments/apex/flights_apex_web_bf_features_in_single_list";
import useRouteName from "../../../hooks/useRouteName";
import flights_apex_web_bf_sellable_bags from "../../../utils/experiments/apex/flights_apex_web_bf_sellable_bags";
import flights_apex_web_show_sellable_and_excluded_seats from "../../../utils/experiments/apex/flights_apex_web_show_sellable_and_excluded_seats";
import { useSelector } from "react-redux";
import { getIsMultiTraveller } from "../../../store/flightDetails/selectors";
import { useGetFareFeaturesSubtitleCopy } from "../../hooks/useGetFareFeaturesSubtitleCopy";
import useIsPostBookingRoute from "../../../hooks/useIsPostBookingRoute";
import flights_apex_web_show_checked_baggage_exclusions from "../../../utils/experiments/apex/flights_apex_web_show_checked_baggage_exclusions";
import { useHasInfantTraveller } from "../../../hooks/useHasInfantTraveller";
import { DollarIcon } from "@bookingcom/bui-assets-react/streamline";
import { getIsSeatMapAvailable } from "../../../store/selectors/checkoutSteps";

const isClient = process.env.BUILD_TARGET === "client";

type Props = {
  brandedFareInfo: UIBrandedFareInfo;
  sellableFeaturesRequiredByAirline?: boolean;
  excludedFeaturesRequiredByAirline?: boolean;
  isPreCheckAndPay?: boolean;
  showDivider?: boolean;
  showHeader?: boolean;
  showDescriptions?: boolean;
  showSubtitles?: boolean;
  compactHeight?: boolean;
  showExcludedFeatures?: boolean;
  areShown?: boolean;
  showChangeDisclaimerStar?: boolean;
  uniqueUpsellFeatures?: UIBrandedFareFeature[];
  travellers?: number;
  hasSellableBag?: boolean;
  showUpsellFeatureUpgradeIcon?: boolean;
};

const FareFeatures = ({
  brandedFareInfo,
  sellableFeaturesRequiredByAirline,
  excludedFeaturesRequiredByAirline = false,
  showHeader,
  showDivider,
  isPreCheckAndPay,
  showDescriptions,
  showSubtitles,
  compactHeight = false,
  showExcludedFeatures = true,
  areShown,
  showChangeDisclaimerStar,
  uniqueUpsellFeatures = [],
  travellers,
  hasSellableBag,
  showUpsellFeatureUpgradeIcon
}: Props) => {
  const i18n = useI18n();
  const { isMobile } = useUserAgent();
  const routeName = useRouteName();
  const isMultiTraveller = useSelector(getIsMultiTraveller);
  const isSeatMapAvailable = useSelector(getIsSeatMapAvailable);

  if (!brandedFareInfo) throw new Error("FareSwitcher: missing branded fare info");

  const showSellableBags = isClient && !!flights_apex_web_bf_sellable_bags.trackWithDefaultStage();
  const showSellableOrExcludedSeats =
    isClient && !!flights_apex_web_show_sellable_and_excluded_seats.trackWithDefaultStage();

  const { headerCopy } = useGetFareFeaturesSubtitleCopy();

  useEffect(() => {
    if (hasSellableBag && isClient)
      flights_apex_web_bf_sellable_bags.trackStages(isMobile, travellers, sellableFeaturesRequiredByAirline);
  }, [hasSellableBag, isMobile, sellableFeaturesRequiredByAirline, travellers]);

  const featuresToRender = useMemo(() => {
    return getFeaturesToRender(brandedFareInfo, {
      includeSellableFeatures: sellableFeaturesRequiredByAirline,
      includeExcludedFeatures: excludedFeaturesRequiredByAirline,
      includeSellableBaggageFeatures: showSellableBags,
      includeSellableOrExcludedSeats: showSellableOrExcludedSeats,
      isPreCheckAndPay
    });
  }, [
    brandedFareInfo,
    sellableFeaturesRequiredByAirline,
    isPreCheckAndPay,
    excludedFeaturesRequiredByAirline,
    showSellableBags,
    showSellableOrExcludedSeats
  ]);

  const allIncludedFeatures = featuresToRender.includedFeatures;
  const sellableCheckedBaggageFeatures = allIncludedFeatures.filter(
    (f) => f.featureName === "CHECK_BAGGAGE" && f.availability === "SELLABLE" && showSellableBags
  );
  const includedAndSellableFeatures = allIncludedFeatures.filter(
    (f) => !(f.featureName === "CHECK_BAGGAGE" && f.availability === "SELLABLE" && showSellableBags)
  );
  const hasIncludedOrSellableFeatures = includedAndSellableFeatures.length > 0;
  const excludedFeatures = showExcludedFeatures ? featuresToRender.nonIncludedFeatures : [];
  const hasExcludedFeatures = excludedFeatures.length > 0;

  /* start - flights_apex_web_show_checked_baggage_exclusions */
  const isPostBookingRoute = useIsPostBookingRoute();
  const isSearchResultsPage = routeName === "searchresults";
  const hasInfantTraveller = useHasInfantTraveller();
  const hasCheckedBaggageExclusion = brandedFareInfo.nonIncludedFeatures?.some(
    (feature) => feature.featureName === "CHECK_BAGGAGE"
  );
  if (areShown !== false && hasCheckedBaggageExclusion && !isPostBookingRoute && !isSearchResultsPage) {
    const isCheckAndPay = routeName === "checkout-payment";
    flights_apex_web_show_checked_baggage_exclusions.trackStages(
      isMobile,
      isMultiTraveller,
      hasInfantTraveller,
      isCheckAndPay,
      excludedFeaturesRequiredByAirline
    );
  }
  /* end - flights_apex_web_show_checked_baggage_exclusions */

  if (!hasIncludedOrSellableFeatures && !hasExcludedFeatures) return null;

  const canUseSingleList = isClient && hasExcludedFeatures && areShown !== false;
  const useSingleList = canUseSingleList && !!flights_apex_web_bf_features_in_single_list.trackWithDefaultStage();

  if (canUseSingleList)
    flights_apex_web_bf_features_in_single_list.trackStages(isMobile, routeName, excludedFeaturesRequiredByAirline);

  let mainFeatures = useSingleList
    ? mergeFeaturesInOrder(includedAndSellableFeatures, excludedFeatures)
    : includedAndSellableFeatures;

  if (!isSeatMapAvailable && !isPreCheckAndPay && showSellableOrExcludedSeats) {
    mainFeatures = mainFeatures.map((feature) => overrideSeatSelectedAtAirlineCopy(feature, i18n));
  }

  return (
    <>
      {showHeader && hasIncludedOrSellableFeatures && headerCopy && (
        <FareFeatureHeading>{i18n.trans(headerCopy)}</FareFeatureHeading>
      )}

      {mainFeatures.map((feature) => (
        <FareFeature
          key={feature.featureName}
          id={feature.featureName}
          IconSVG={feature.icon}
          content={
            feature.label + (showChangeDisclaimerStar && CHANGE_FEATURES.includes(feature.featureName) ? "*" : "")
          }
          description={showDescriptions ? feature.description : undefined}
          subtitle={showSubtitles ? feature.subtitle : undefined}
          availability={feature.availability}
          compactHeight={compactHeight}
          isUniqueUpsellFeature={uniqueUpsellFeatures.some(
            (upsellFeature) => upsellFeature.featureName === feature.featureName
          )}
          showUpsellFeatureUpgradeIcon={showUpsellFeatureUpgradeIcon}
        />
      ))}

      {!useSingleList && showDivider && hasExcludedFeatures && hasIncludedOrSellableFeatures && (
        <Frame mb={4}>
          <Divider className={styles.divider} attributes={{ "data-testid": "fareFeatureDivider" }} />
        </Frame>
      )}

      {sellableCheckedBaggageFeatures.map((feature) => (
        <FareFeature
          key={feature.featureName}
          id={feature.featureName}
          IconSVG={DollarIcon}
          content={feature.label}
          availability={feature.availability}
          compactHeight={compactHeight}
          isUniqueUpsellFeature={uniqueUpsellFeatures.some(
            (upsellFeature) => upsellFeature.featureName === feature.featureName
          )}
          showUpsellFeatureUpgradeIcon={showUpsellFeatureUpgradeIcon}
        />
      ))}

      {!useSingleList &&
        excludedFeatures.map((feature) => (
          <FareFeature
            key={feature.featureName}
            content={feature.label}
            availability={feature.availability}
            IconSVG={feature.icon}
            compactHeight={compactHeight}
          />
        ))}
    </>
  );
};

export default FareFeatures;
